import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import UsersIcon from 'assets/Icons/sidebarIcons/UsersIcon';
import GeneratorInput from 'components/elements/Form/Input/GeneratorInput';
import TimezoneDateTimeRangePicker from 'components/elements/Form/Input/TimezoneDateTimeRangePicker';
import Input from 'components/elements/Form/Input/input';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import MultiSelectInput from 'components/elements/Form/select/multiSelectInput';
import SelectInput from 'components/elements/Form/select/selectInput';
import Modal from 'components/elements/Modal';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSaveCard from 'components/structure/Card/CardForms/crud/useSave';
import { formatRFC3339 } from 'date-fns';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import serviceApi from 'services/api';
import auth from 'services/auth';
import { useAuth } from 'services/auth-service';
import AccountService, { AccountProps } from 'services/hooks/account-service';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import CardService, { CardProps, IntegrationCardDetails } from 'services/hooks/card-service';
import IntegrationService, { IntegrationProps } from 'services/hooks/integration-service';
import { OccupantProps } from 'services/hooks/occupant-service';
import OrganizationService, { OrganizationProps } from 'services/hooks/organization-service';
import SeamAssaVisionlineService, {
  SeamVisionlineDoorsProps,
  SeamVisionlinePhoneProps
} from 'services/hooks/seam-assa-visionline-service';
import {
  assaCardTypes,
  assaGuestCardTypes,
  AssaGuestCardTypeValue,
  CardFormat,
  cardFormatLabels,
  colours,
  defaultDateTimeUTCValue,
  AccessIntegrationName,
  CardToggleOptions,
  GetNonServerErrorMessage
} from 'utils/constants';
import {
  generateApiFailErrorMessage,
  generateSaveErrorMessage,
  generateSaveSuccessMessage,
  generateUpdateErrorMessage,
  generateUpdateSuccessMessage
} from 'utils/helpers';
import * as yup from 'yup';
import useSaveOccupant from './crud/useSave';
import { GetOccupantDefaultValues } from './getOccupantDefaultValues';
import RoleSelectionForm from './roleSelectionform';
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase';
import { HelpTooltipIcon } from 'components/elements/Help/HelpTooltipIcon';
import { Box, ToggleButton, styled } from '@mui/material';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import FormatVisionlineDoorsTreeData from 'utils/visionlineDoorsFormatter';
import NestedAntdMultiSelect, {
  DataNode
} from 'components/elements/Form/select/NestedAntdMultiSelect';
import LatchService, { LatchDoorProps } from 'services/hooks/latch-service';
import FormatLatchDoorsTreeData from 'utils/latchDoorsFormatter';
import CustomPhoneInput from 'components/elements/Form/Input/PhoneInput';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import CheckboxInput from 'components/elements/Form/Input/CheckboxInput';

const api = process.env.REACT_APP_ENVIRONMENT === 'local' ? auth : serviceApi;

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: any;
  updateQuery: any;
  editQuery?: any;
  editUUID?: string;
  queryName: string;
};

// Styling for toggle button group
const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0),
    border: 0,
    borderRadius: '50px',
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0
    }
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
    marginLeft: 1,
    borderLeft: '1px solid transparent'
  }
}));

// Define a base set of card fields in occupant form
const baseCardFields: string[] = [
  'format',
  'description',
  'badgeId',
  'facilityCode',
  'doorIds',
  'startDate',
  'endDate',
  'integrationCardDetails.assaVisionline.cardType',
  'integrationCardDetails.assaVisionline.guestCardType',
  'latchDoorIds',
  'latchStartDate',
  'latchEndDate',
  'cards'
];

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  const [getOccupantResult, setGetOccupantResult] = useState<OccupantProps | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [emailRequired, setEmailRequired] = useState(false);
  const [selectedCardFormat, setSelectedCardFormat] = useState<CardFormat | null>(null);
  const [cardFormatExists, setCardFormatExists] = useState<string | null>(null);
  const [cardDefaultValues, setCardDefaultValues] = useState<any[]>([]);
  const [isCardDefaultValuesLoaded, setIsCardDefaultValuesLoaded] = useState(false);
  const [isAVLIntegration, setIsAVLIntegration] = useState(false);
  const [isW26Integration, setIsW26Integration] = useState(false);
  const [buildingTimezone, setBuildingTimezone] = useState<any>(null);
  const [visionlineDoorsData, setVisionlineDoorsData] = useState<SeamVisionlineDoorsProps[] | null>(
    null
  );
  const [doorOptions, setDoorOptions] = useState<DataNode[]>([]);
  const [acsSystemId, setAcsSystemId] = useState<string | undefined>(undefined);
  const [phoneOptions, setPhoneOptions] = useState<{ value: string; label: string }[]>([]);
  const [phoneDeactivateDisbaled, setPhoneDeactivateDisabled] = useState(false);
  const [isGuestAssaCardType, setIsGuestAssaCardType] = useState<boolean>(false);
  const [latchDoorsData, setLatchDoorsData] = useState<LatchDoorProps | null>(null);
  const [latchDoorOptions, setLatchDoorOptions] = useState<DataNode[]>([]);
  // State to manage the selection of access card options
  const [cardToggleOption, setCardToggleOption] = useState<string>(CardToggleOptions.NoCard);
  const [selectedIntegration, setSelectedIntegration] = useState<AccessIntegrationName | null>(
    null
  );

  const {
    state: { user }
  } = useAuth();

  const formatedOptions = (value: any, label: string) => {
    return { value: value, label: label };
  };

  const walletNameRegex = /^[a-zA-Z0-9\s'-]*$/;

  const CreateOccupantSchema = yup.object().shape({
    phoneNumber: yup
      .string()
      .required('Phone number is required')
      .test('is-possible-phone-number', 'Phone number is not valid', (value) =>
        value ? isPossiblePhoneNumber(value) : true
      ),
    firstName: yup
      .string()
      .required('First Name is required')
      .when([], {
        is: () => selectedIntegration === AccessIntegrationName.WavelynxWallet,
        then: (schema) =>
          schema.matches(
            walletNameRegex,
            'First name can only contain alphanumeric characters, spaces, dashes, and apostrophes'
          ),
        otherwise: (schema) => schema
      }),
    lastName: yup
      .string()
      .required('Last Name is required')
      .when([], {
        is: () => selectedIntegration === AccessIntegrationName.WavelynxWallet,
        then: (schema) =>
          schema.matches(
            walletNameRegex,
            'Last name can only contain alphanumeric characters, spaces, dashes, and apostrophes'
          ),
        otherwise: (schema) => schema
      }),
    organizationUuid: yup.string().required('Organization is required'),
    buildingUuid: yup.string().required('Building is required'),
    accountUuid: yup.string().required('Account is required'),
    integrationUuid: yup.string().required('Integration is required'),
    email: yup
      .string()
      .email()
      .when([], {
        is: () => emailRequired,
        then: yup.string().required('Email is required'),
        otherwise: yup.string().notRequired()
      }),
    badgeId: yup.string().when({
      is: () =>
        !!cardFormatExists &&
        (selectedCardFormat === CardFormat.W26 || selectedCardFormat === CardFormat.WAVELYNXWALLET),
      then: yup.string().required('Badge Id is required'),
      otherwise: yup.string().notRequired()
    }),
    facilityCode: yup.string().when([], {
      is: () =>
        !!cardFormatExists &&
        (selectedCardFormat === CardFormat.W26 || selectedCardFormat === CardFormat.WAVELYNXWALLET),
      then: yup.string().required('Facility Code is required'),
      otherwise: yup.string().notRequired()
    }),
    doorIds: yup.array().when([], {
      is: () => !!cardFormatExists && selectedCardFormat === CardFormat.AVL,
      then: yup.array().min(1, 'Door is required'),
      otherwise: yup.array().notRequired()
    }),
    integrationCardDetails: yup
      .object()
      .shape({
        assaVisionline: yup
          .object()
          .shape({
            cardType: yup.string().when([], {
              is: () => !!cardFormatExists && selectedCardFormat === CardFormat.AVL,
              then: yup.string().required('Assa card type is required'),
              otherwise: yup.string().notRequired()
            })
          })
          .default(undefined)
      })
      .default(undefined),
    startDate: yup.date().when([], {
      is: () => !!cardFormatExists && selectedCardFormat === CardFormat.AVL,
      then: yup.date().required('Check-in date & time is required'),
      otherwise: yup.date().notRequired()
    }),
    endDate: yup.date().when([], {
      is: () => !!cardFormatExists && selectedCardFormat === CardFormat.AVL,
      then: yup
        .date()
        .required('Check-out date & time is required')
        .test('is-greater', 'Check-out time must be after check-in time', function (value) {
          const { startDate } = this.parent;
          return startDate && value && value > startDate;
        }),
      otherwise: yup.date().notRequired()
    }),
    latchDoorIds: yup.array().when([], {
      is: () => !!cardFormatExists && selectedCardFormat === CardFormat.LATCH,
      then: yup.array().min(1, 'Door is required'),
      otherwise: yup.array().notRequired()
    }),
    latchStartDate: yup.date().when([], {
      is: () => !!cardFormatExists && selectedCardFormat === CardFormat.LATCH,
      then: yup.date().required('Check-in date & time is required'),
      otherwise: yup.date().notRequired()
    }),
    latchEndDate: yup.date().when([], {
      is: () => !!cardFormatExists && selectedCardFormat === CardFormat.LATCH,
      then: yup
        .date()
        .required('Check-out date & time is required')
        .test('is-greater', 'Check-out time must be after check-in time', function (value) {
          const { latchStartDate } = this.parent;
          return latchStartDate && value && value > latchStartDate;
        }),
      otherwise: yup.date().notRequired()
    })
  });

  const renderDefaultValues = (occupantData: any) => {
    if (occupantData?.cards) {
      return Object.assign(occupantData?.cards).map(
        (item: { badgeId: number; description: string }) =>
          formatedOptions(item, `(${item?.badgeId}) ${item?.description}`)
      );
    }
  };

  // Fetch user specific Occupant data
  const { isSuccess, data, refetch } = useQuery<OccupantProps, Error>(
    'occupant',
    () => editQuery(editUUID),
    {
      enabled: editUUID ? true : false,
      onSuccess: (occupantData: any) => {
        const cardDefVals = renderDefaultValues(occupantData);
        setCardDefaultValues(cardDefVals);
        setIsCardDefaultValuesLoaded(true);
      },
      onError: () => {
        toast.error(generateApiFailErrorMessage('Occupant'));
      }
    }
  );

  // Fetch user specific Account data
  const { data: accountData } = useQuery<AccountProps, Error>(
    'account',
    () => AccountService.findAccountById(user.uuid),
    {
      enabled: user.role === 'ACCOUNT',
      onError: () => {
        toast.error(generateApiFailErrorMessage('Account'));
      }
    }
  );

  // Fetch user specific Organization data
  const { data: orgData } = useQuery<OrganizationProps, Error>(
    'organization',
    () => OrganizationService.findOrgById(user.uuid),
    {
      enabled: user.role === 'ORG',
      onError: () => {
        toast.error(generateApiFailErrorMessage('Organization'));
      }
    }
  );

  // Fetch user specific Building data
  const { data: buildingData } = useQuery<BuildingProps, Error>(
    'building',
    () =>
      BuildingService.findBuildingById(orgData?.buildingUuid ? orgData.buildingUuid : user.uuid),
    {
      enabled: user.role === 'BUILDING',
      onError: () => {
        toast.error(generateApiFailErrorMessage('Building'));
      }
    }
  );

  const methods = useForm({
    defaultValues:
      getOccupantResult && Array.isArray(getOccupantResult)
        ? {
            uuid: getOccupantResult?.uuid,
            phoneNumber: getOccupantResult?.phoneNumber,
            firstName: getOccupantResult?.firstName,
            lastName: getOccupantResult?.lastName,
            isVisitor: getOccupantResult?.isVisitor,
            isStaff: getOccupantResult?.isStaff,
            organizationUuid: getOccupantResult?.organizationUuid,
            buildingUuid: getOccupantResult?.buildingUuid,
            accountUuid: getOccupantResult?.accountUuid,
            cards: getOccupantResult?.cards,
            format: getOccupantResult?.format,
            badgeId: getOccupantResult?.badgeId,
            facilityCode: getOccupantResult?.facilityCode,
            description: getOccupantResult?.description,
            integrationUuid: getOccupantResult?.integrationUuid,
            email: getOccupantResult?.email,
            startTime: Number.isNaN(Date.parse(getOccupantResult?.startTime))
              ? undefined
              : new Date(getOccupantResult?.startTime),
            endTime: Number.isNaN(Date.parse(getOccupantResult?.endTime))
              ? undefined
              : new Date(getOccupantResult?.endTime),
            fcmToken: getOccupantResult?.fcmToken,
            credentialId: getOccupantResult?.credentialId,
            doorIds: getOccupantResult?.doorIds,
            startDate:
              getOccupantResult?.startDate !== defaultDateTimeUTCValue
                ? new Date(getOccupantResult?.startDate)
                : moment.tz(new Date(), buildingTimezone).toDate(),
            endDate:
              getOccupantResult?.endDate !== defaultDateTimeUTCValue
                ? new Date(getOccupantResult?.endDate)
                : moment.tz(new Date(), buildingTimezone).toDate(),
            latchDoorIds: getOccupantResult?.doorIds,
            latchStartDate:
              getOccupantResult?.startDate !== defaultDateTimeUTCValue
                ? new Date(getOccupantResult?.startDate)
                : moment.tz(new Date(), buildingTimezone).toDate(),
            latchEndDate:
              getOccupantResult?.endDate !== defaultDateTimeUTCValue
                ? new Date(getOccupantResult?.endDate)
                : moment.tz(new Date(), buildingTimezone).toDate()
          }
        : GetOccupantDefaultValues(
            user?.role,
            accountData,
            orgData,
            buildingData,
            buildingTimezone
          ),
    resolver: yupResolver(CreateOccupantSchema)
  });

  useEffect(() => {
    if (user?.role === 'BUILDING' && buildingData) {
      methods.setValue('accountUuid', buildingData?.accountUuid);
    }
    if (user?.role === 'ORG' && orgData && buildingData) {
      methods.setValue('buildingUuid', orgData?.buildingUuid);
      methods.setValue('accountUuid', buildingData?.accountUuid);
    }
  }, [buildingData, methods, orgData, user.role]);

  useEffect(() => {
    if (data && editUUID) {
      refetch();
      setGetOccupantResult(data);
      methods.reset({
        uuid: data?.uuid,
        phoneNumber: '+' + data?.phoneNumber,
        firstName: data?.firstName,
        lastName: data?.lastName,
        isVisitor: data?.isVisitor,
        isStaff: data?.isStaff,
        organizationUuid: data?.organizationUuid,
        buildingUuid: data?.buildingUuid,
        accountUuid: data?.accountUuid,
        cards: data?.cards,
        format: data?.format,
        badgeId: data?.badgeId,
        facilityCode: data?.facilityCode,
        description: data?.description,
        integrationUuid: data?.integrationUuid,
        email: data?.email,
        startTime: Number.isNaN(Date.parse(data?.startTime))
          ? undefined
          : new Date(data?.startTime),
        endTime: Number.isNaN(Date.parse(data?.endTime)) ? undefined : new Date(data?.endTime),
        doorIds: data?.doorIds,
        startDate:
          selectedCardFormat === CardFormat.AVL && data?.startDate !== defaultDateTimeUTCValue
            ? new Date(data?.startDate)
            : moment.tz(new Date(), buildingTimezone).toDate(),
        endDate:
          selectedCardFormat === CardFormat.AVL && data?.endDate !== defaultDateTimeUTCValue
            ? new Date(data?.endDate)
            : moment.tz(new Date(), buildingTimezone).toDate(),
        latchDoorIds: data?.doorIds,
        latchStartDate:
          selectedCardFormat === CardFormat.LATCH && data?.startDate !== defaultDateTimeUTCValue
            ? new Date(data?.startDate)
            : moment.tz(new Date(), buildingTimezone).toDate(),
        latchEndDate:
          selectedCardFormat === CardFormat.LATCH && data?.endDate !== defaultDateTimeUTCValue
            ? new Date(data?.endDate)
            : moment.tz(new Date(), buildingTimezone).toDate()
      });
    }
  }, [buildingTimezone, data, editUUID, methods, refetch, selectedCardFormat]);

  // saveOccupantMutation will be used to perform CRUD operations on the Occupant data.
  const saveOccupantMutation = useSaveOccupant({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: (res: { cards: any[]; uuid: any }) => {
      if (
        res?.cards?.every(
          (card: { mobileUserUuid: string }) =>
            card?.mobileUserUuid === '00000000-0000-0000-0000-000000000000'
        )
      ) {
        const newArr = {
          ...res,
          cards: res?.cards?.map((card: { uuid: string }) => {
            return { ...card, mobileUserUuid: res?.uuid };
          })
        };
        api.put<OccupantProps>(`/apiv1/admin/occupant/${newArr?.uuid}`, newArr);
      }
      setShowModal(false);
      setDisabled(false);

      if (!editUUID) {
        toast.success(generateSaveSuccessMessage('Occupant'));
      } else {
        toast.success(generateUpdateSuccessMessage('Occupant'));
      }

      if (setEditUUID) setEditUUID('');
    }
  });

  // deactivatePhoneMutation will be used to deactivate phone
  const deactivatePhoneMutation = useSaveOccupant({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setPhoneDeactivateDisabled(false);

      if (editUUID) {
        // Show success toast notification on successfully deactivating phone
        toast.success('Deactivated Phone successfully!');
      }

      if (setEditUUID) setEditUUID('');
    }
  });

  // saveCardMutation will be used to perform CRUD operations on the Card data
  const saveCardMutation = useSaveCard({
    createQuery: CardService.createCard,
    updateQuery: CardService.updateCard,
    queryName: 'cards',
    onSuccess: () => {
      setShowModal(false);
      if (setEditUUID) setEditUUID('');
    },
    onError: (error: any) => {
      if (error.response) {
        const errorMessage =
          error.response.data?.error ||
          error.response.data?.message ||
          'An unexpected error occurred.';

        switch (error.response.status) {
          case 400:
            toast.error(errorMessage, { autoClose: 15000 });
            break;
          default:
            if (!editUUID) {
              toast.error(generateSaveErrorMessage('Card'));
            } else {
              toast.error(generateUpdateErrorMessage('Card'));
            }
            break;
        }
      } else {
        toast.error(GetNonServerErrorMessage);
      }
    }
  });

  const [buildingInfo, setbuildingInfo] = useState<BuildingProps | null>(null);
  const [cardsInfo, setcardsInfo] = useState<any>(null);
  const [cardOptions, setCardOptions] = useState<any>([]);

  const orgUuid = methods.watch('organizationUuid');
  // Fetch specific Organization data based on the selected organizationUuid
  const { data: organizationdata } = useQuery<OrganizationProps, Error>(
    ['organization', orgUuid],
    () => OrganizationService.findOrgById(orgUuid),
    {
      enabled: orgUuid ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Organization'));
      }
    }
  );

  // Fetch specific Building data based on buildingUuid
  const { data: buildData } = useQuery<BuildingProps, Error>(
    ['building', organizationdata?.buildingUuid],
    () => BuildingService.findBuildingById(organizationdata?.buildingUuid),
    {
      enabled: organizationdata?.buildingUuid ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Building'));
      }
    }
  );

  // Fetch all Cards data
  const { data: cardsData } = useQuery<CardProps[], Error>(
    'cards',
    () => CardService.findAllCards(),
    {
      enabled: organizationdata?.buildingUuid ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Cards'));
      }
    }
  );

  //Save the state using useState()
  useEffect(() => {
    if (buildData) {
      setbuildingInfo(buildData);
    } else {
      setbuildingInfo(null);
    }
    if (cardsData) {
      setcardsInfo(cardsData);
    }
  }, [buildData, cardsData]);

  useEffect(() => {
    if (buildingInfo) {
      setBuildingTimezone(buildingInfo.timezone);
    } else {
      setBuildingTimezone(null);
    }
  }, [buildingInfo]);

  //Provide all UNASSIGNED Cards options to the user based on selected Organization
  useEffect(() => {
    if (cardsInfo && orgUuid) {
      const populateUnassignedCards: { value: string; label: string }[] = [];
      cardsInfo?.map(
        (card: {
          uuid: string;
          organizationUuid: string;
          status: string;
          badgeId: string;
          description: string;
        }) =>
          card?.organizationUuid === orgUuid && card?.status === 'UNASSIGNED'
            ? populateUnassignedCards.push(
                formatedOptions(card, '(' + card?.badgeId + ') ' + card?.description)
              )
            : null
      );
      setCardOptions(populateUnassignedCards);
    }
  }, [cardsInfo, orgUuid]);

  useEffect(() => {
    if (organizationdata?.integrationDetails?.acsSystemId) {
      setAcsSystemId(organizationdata?.integrationDetails?.acsSystemId);
    } else {
      setAcsSystemId(undefined);
    }
  }, [organizationdata]);

  // Fetch visionline doors
  const { data: doorsData } = useQuery<SeamVisionlineDoorsProps[], Error>(
    ['doors', acsSystemId, selectedCardFormat === CardFormat.AVL],
    () => SeamAssaVisionlineService.findAllVisionlineDoors(acsSystemId),
    {
      enabled: selectedCardFormat === CardFormat.AVL && !!acsSystemId,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Doors'));
      }
    }
  );

  useEffect(() => {
    if (doorsData) {
      setVisionlineDoorsData(doorsData);
    } else {
      setVisionlineDoorsData(null);
    }
  }, [doorsData]);

  useEffect(() => {
    if (visionlineDoorsData) {
      const formattedDoors = FormatVisionlineDoorsTreeData(visionlineDoorsData);
      setDoorOptions(formattedDoors); // Update the state with the new data
    } else {
      setDoorOptions([]); // Set to empty array if there is no data
    }
  }, [visionlineDoorsData]);

  // Fetch Latch Doors
  const { data: latchDoors } = useQuery<LatchDoorProps, Error>(
    ['latchDoors', orgUuid, selectedCardFormat === CardFormat.LATCH],
    () => LatchService.retrieveDoors(orgUuid),
    {
      enabled: selectedCardFormat === CardFormat.LATCH && !!orgUuid,
      onError: () => {
        toast.error('Please select an organization with Latch integration to see door options.');
      }
    }
  );

  useEffect(() => {
    if (latchDoors) {
      setLatchDoorsData(latchDoors);
    } else {
      setLatchDoorsData(null);
    }
  }, [latchDoors]);

  useEffect(() => {
    if (latchDoorsData) {
      const formattedDoors = FormatLatchDoorsTreeData(latchDoorsData);
      setLatchDoorOptions(formattedDoors); // Update the state with the new data
    } else {
      setLatchDoorOptions([]); // Set to empty array if there is no data
    }
  }, [latchDoorsData]);

  // Making 'badgeId' and 'facilityCode' as not required fields when 'ICT' format is selected
  const cardFormatWatch = methods.watch('format');

  useEffect(() => {
    if (cardFormatWatch) {
      setCardFormatExists(cardFormatWatch);
    } else {
      setCardFormatExists(null);
    }
  }, [cardFormatWatch]);

  /* The below code is using the `watch` method to monitor changes to the 'format' property. 
  When the 'format' property changes, the component will update the selected card format based on the value of the 'format' property. */
  useEffect(() => {
    switch (cardFormatWatch) {
      case CardFormat.W26:
        setSelectedCardFormat(CardFormat.W26);
        break;
      case CardFormat.AVL:
        setSelectedCardFormat(CardFormat.AVL);
        break;
      case CardFormat.LATCH:
        setSelectedCardFormat(CardFormat.LATCH);
        break;
      case CardFormat.WAVELYNXWALLET:
        setSelectedCardFormat(CardFormat.WAVELYNXWALLET);
        break;
      case CardFormat.ICT:
        setSelectedCardFormat(CardFormat.ICT);
        break;
      default:
        setSelectedCardFormat(null);
    }
  }, [cardFormatWatch]);

  // Displaying Email field and making it a required field only when email is set to True for that Integration Preference.
  const [integrationResult, setIntegrationResult] = useState<IntegrationProps | null>(null);
  const integrationUuidWatch = methods.watch('integrationUuid');
  const { data: integrationData } = useQuery<IntegrationProps, Error>(
    ['integration', integrationUuidWatch],
    () => IntegrationService.findIntegrationById(integrationUuidWatch),
    {
      enabled: integrationUuidWatch ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Integration'));
      }
    }
  );

  // Reset `format` field when selected integration changes
  useEffect(() => {
    methods.setValue('format', '');
  }, [integrationUuidWatch, methods]);

  // Convert `CardFormat` enum to an array of objects suitable for card format dropdown options
  const enumToDropdownOptions = Object.values(CardFormat).map((value) => ({
    value,
    label: cardFormatLabels[value]
  }));

  // Filter Card Format options based on the selected Integration Type property
  const filterCardFormats = (integrationName: string | undefined) => {
    let options = enumToDropdownOptions;

    if (integrationName === AccessIntegrationName.Wavelynx) {
      options = options.filter((option) => option.value === CardFormat.W26);
    } else if (integrationName === AccessIntegrationName.AssaVisionline) {
      options = options.filter((option) => option.value === CardFormat.AVL);
    } else if (integrationName === AccessIntegrationName.Latch) {
      options = options.filter((option) => option.value === CardFormat.LATCH);
    } else if (integrationName === AccessIntegrationName.WavelynxWallet) {
      options = options.filter((option) => option.value === CardFormat.WAVELYNXWALLET);
    } else if (integrationName === AccessIntegrationName.ICT) {
      options = options.filter((option) => option.value === CardFormat.ICT);
    } else if (integrationName === AccessIntegrationName.None) {
      options = [];
    }
    return options;
  };

  useEffect(() => {
    if (integrationData) {
      setIntegrationResult(integrationData);
    } else {
      setIntegrationResult(null);
    }

    if (integrationResult?.name === AccessIntegrationName.AssaVisionline) {
      setIsAVLIntegration(true);
    } else {
      setIsAVLIntegration(false);
    }

    if (integrationResult?.name === AccessIntegrationName.Wavelynx) {
      setIsW26Integration(true);
    } else {
      setIsW26Integration(false);
    }

    if (integrationData?.name === AccessIntegrationName.None) {
      setCardToggleOption(CardToggleOptions.NoCard);
    }

    if (integrationResult?.preferences?.emailRequired === true) {
      setEmailRequired(true);
    } else {
      setEmailRequired(false);
    }

    switch (integrationData?.name) {
      case AccessIntegrationName.Wavelynx:
        setSelectedIntegration(AccessIntegrationName.Wavelynx);
        break;
      case AccessIntegrationName.AssaVisionline:
        setSelectedIntegration(AccessIntegrationName.AssaVisionline);
        break;
      case AccessIntegrationName.Latch:
        setSelectedIntegration(AccessIntegrationName.Latch);
        break;
      case AccessIntegrationName.WavelynxWallet:
        setSelectedIntegration(AccessIntegrationName.WavelynxWallet);
        break;
      case AccessIntegrationName.ICT:
        setSelectedIntegration(AccessIntegrationName.ICT);
        break;
      case AccessIntegrationName.None:
        setSelectedIntegration(AccessIntegrationName.None);
        break;
      default:
        setSelectedIntegration(null);
    }
  }, [integrationData, integrationResult]);

  // Handle changing the toggle card option
  const handleCardOptionChange = (
    _event: React.MouseEvent<HTMLElement>,
    newCardToggleOption: string | null
  ) => {
    if (newCardToggleOption !== null) {
      setCardToggleOption(newCardToggleOption);
    }
  };

  const resetFields = useCallback(
    (fields: string[], excludeFields: string[] = []) => {
      const defaultValues = GetOccupantDefaultValues(
        user?.role,
        accountData,
        orgData,
        buildingData,
        buildingTimezone
      );
      fields.forEach((field) => {
        if (!excludeFields.includes(field)) {
          methods.setValue(field, defaultValues[field]);
        }
      });
    },
    [user?.role, accountData, orgData, buildingData, buildingTimezone, methods]
  );

  const resetFieldsForNoCard = useCallback(() => {
    resetFields(baseCardFields);
  }, [resetFields]);

  const resetFieldsForNewCard = useCallback(() => {
    resetFields(['cards']);
  }, [resetFields]);

  const resetFieldsForExistingCard = useCallback(() => {
    resetFields(baseCardFields, ['cards']);
  }, [resetFields]);

  // Handle resetting fields when cardToggleOption changes
  useEffect(() => {
    if (!editUUID) {
      if (cardToggleOption === CardToggleOptions.NoCard) {
        resetFieldsForNoCard();
      } else if (cardToggleOption === CardToggleOptions.NewCard) {
        resetFieldsForNewCard();
      } else if (cardToggleOption === CardToggleOptions.ExistingCard) {
        resetFieldsForExistingCard();
      }
    }
  }, [
    cardToggleOption,
    editUUID,
    resetFieldsForNoCard,
    resetFieldsForNewCard,
    resetFieldsForExistingCard
  ]);

  // Reset card option if integration is not `isW26Integration` and 'existingCard' is the active selection from the card toggle options
  useEffect(() => {
    if (!isW26Integration && cardToggleOption === CardToggleOptions.ExistingCard) {
      setCardToggleOption(CardToggleOptions.NoCard); // Reset to default option
    }
  }, [isW26Integration, cardToggleOption]);

  // Fetch phones data of the selected Occupant when the form is in Edit mode and the Occupant is of AVL integration
  const { data: phonesData } = useQuery<SeamVisionlinePhoneProps[], Error>(
    ['phones', editUUID, isAVLIntegration],
    () => SeamAssaVisionlineService.findAllOccupantPhones(editUUID),
    {
      enabled: isAVLIntegration && !!editUUID,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Phone'));
      }
    }
  );

  // Provide all phone options based on selected Occupant
  useEffect(() => {
    if (phonesData) {
      const populatePhoneOptions: { value: string; label: string }[] = [];
      phonesData?.map((phone: { deviceId: string; deviceType: string }) =>
        populatePhoneOptions.push(formatedOptions(phone.deviceId, phone.deviceType))
      );
      setPhoneOptions(populatePhoneOptions);
    }
  }, [phonesData]);

  // Watch method to track the selected 'integrationCardDetails.assaVisionline.cardType' filed value
  const assaCardTypeWatch = methods.watch('integrationCardDetails.assaVisionline.cardType');

  useEffect(() => {
    if (assaCardTypeWatch == AssaGuestCardTypeValue) {
      setIsGuestAssaCardType(true);
    } else {
      setIsGuestAssaCardType(false);
    }
  }, [assaCardTypeWatch]);

  // If `isAVLFormat` is true then set the value for `integrationCardDetails.assaVisionline.guestCardType` to 'joiner'
  useEffect(() => {
    if (selectedCardFormat === CardFormat.AVL && isGuestAssaCardType) {
      methods.setValue(
        'integrationCardDetails.assaVisionline.guestCardType',
        assaGuestCardTypes[1].value
      );
    } else {
      methods.setValue('integrationCardDetails.assaVisionline.guestCardType', '');
    }
  }, [selectedCardFormat, isGuestAssaCardType, methods]);

  // Disable Deactivate Phone button if Lost Phone is not selected
  const lostPhoneIdWatch = methods.watch('lostPhoneId');
  useEffect(() => {
    if (lostPhoneIdWatch) {
      setPhoneDeactivateDisabled(false);
    } else {
      setPhoneDeactivateDisabled(true);
    }
  }, [lostPhoneIdWatch]);

  const handleDeactivatePhone = (e: OccupantProps) => {
    setPhoneDeactivateDisabled(true);

    // If Occupant clicks on 'Deactivate Phone' button without selecting the lost phone, display an error message to the Occupant
    if (!e.lostPhoneId) {
      setPhoneDeactivateDisabled(false);
      toast.error('Please select the lost phone to deactivate!');
      return;
    }

    const occupantData = getOccupantResult && {
      ...getOccupantResult,
      lostPhoneId: e?.lostPhoneId
    };
    if (occupantData) {
      try {
        deactivatePhoneMutation.mutateAsync({
          uuid: occupantData?.uuid,
          firstName: occupantData?.firstName,
          lastName: occupantData?.lastName,
          phoneNumber: occupantData?.phoneNumber,
          isVisitor: occupantData?.isVisitor,
          isStaff: occupantData?.isStaff,
          accountUuid: occupantData?.accountUuid,
          buildingUuid: occupantData?.buildingUuid,
          organizationUuid: occupantData?.organizationUuid,
          cards: occupantData?.cards,
          fcmToken: occupantData?.fcmToken,
          credentialId: occupantData?.credentialId,
          integrationUuid: occupantData?.integrationUuid,
          email: occupantData?.email,
          startTime: occupantData?.startTime
            ? formatRFC3339(new Date(occupantData?.startTime))
            : null,
          endTime: occupantData?.endTime ? formatRFC3339(new Date(occupantData?.endTime)) : null,
          lostPhoneId: occupantData?.lostPhoneId
        });
      } catch (error) {
        setPhoneDeactivateDisabled(false);
        toast.error('Failed to deactivate phone. Please try again');
      }
    }
  };

  // User data will get stored in DB using saveOccupantMutation and saveCardMutation
  const onSubmit = async (e: OccupantProps) => {
    setDisabled(true);
    let tempcardid: any = null;

    try {
      tempcardid = await saveOccupantMutation.mutateAsync({
        uuid: e?.uuid,
        firstName: e?.firstName,
        lastName: e?.lastName,
        phoneNumber: e?.phoneNumber.replace(/^\+/, ''),
        isVisitor: e?.isVisitor,
        isStaff: e?.isStaff,
        accountUuid: e?.accountUuid,
        buildingUuid: e?.buildingUuid,
        organizationUuid: e?.organizationUuid,
        cards:
          e?.uuid && e?.cards
            ? e?.cards.map((card: { mobileUserUuid: any }) => {
                return { ...card, mobileUserUuid: e?.uuid };
              })
            : e?.cards,
        fcmToken: 'token',
        credentialId: 'cred',
        integrationUuid: e?.integrationUuid,
        email: e?.email,
        startTime: !Number.isNaN(Date.parse(e?.startTime))
          ? formatRFC3339(new Date(e?.startTime))
          : null,
        endTime: !Number.isNaN(Date.parse(e?.endTime)) ? formatRFC3339(new Date(e?.endTime)) : null
      });
    } catch (error: any) {
      setDisabled(false);
      if (error.response) {
        const errorMessage =
          error.response.data?.error ||
          error.response.data?.message ||
          'An unexpected error occurred.';

        // Check the status code to determine the type of error
        switch (error.response.status) {
          case 409: // Display conflict errors
            toast.error(errorMessage, { autoClose: 15000 });
            break;
          default:
            // If the editUUID is undefined, it means the user is attempting to create a new occupant
            if (!editUUID) {
              toast.error(generateSaveErrorMessage('Occupant'));
            } else {
              // Otherwise, the user is trying to update an existing occupant
              toast.error(generateUpdateErrorMessage('Occupant'));
            }
            break;
        }
      } else {
        // This handles cases where the error is not from the server response
        toast.error(GetNonServerErrorMessage);
      }
    }

    if (!!cardFormatExists && tempcardid?.uuid && organizationdata?.buildingUuid) {
      let selectedDoorIds: string[] = [];
      let selectedStartDate: string | null = null;
      let selectedEndDate: string | null = null;

      // Ensure selectedCardFormat is not null before proceeding
      if (selectedCardFormat !== null) {
        // Map the correct values based on the selected card format
        const doorIdMappings: Partial<Record<CardFormat, () => string[]>> = {
          [CardFormat.AVL]: () => e.doorIds || [],
          [CardFormat.LATCH]: () => e.latchDoorIds || []
        };

        // Map the correct values based on the selected card format
        const dateMappings: Partial<
          Record<CardFormat, { startDate: () => string | null; endDate: () => string | null }>
        > = {
          [CardFormat.AVL]: {
            startDate: () => (e.startDate ? formatRFC3339(e.startDate) : null),
            endDate: () => (e.endDate ? formatRFC3339(e.endDate) : null)
          },
          [CardFormat.LATCH]: {
            startDate: () => (e.latchStartDate ? formatRFC3339(e.latchStartDate) : null),
            endDate: () => (e.latchEndDate ? formatRFC3339(e.latchEndDate) : null)
          }
        };

        // Use the mappings if they exist for the selected card format
        selectedDoorIds = doorIdMappings[selectedCardFormat]?.() || [];
        selectedStartDate = dateMappings[selectedCardFormat]?.startDate() || null;
        selectedEndDate = dateMappings[selectedCardFormat]?.endDate() || null;
      }

      const integrationCardDetails: IntegrationCardDetails = {
        ...((e?.integrationCardDetails?.assaVisionline?.cardType ||
          e?.integrationCardDetails?.assaVisionline?.guestCardType) && {
          assaVisionline: e.integrationCardDetails.assaVisionline
        })
      };

      saveCardMutation.mutate({
        uuid: e?.uuid,
        description: e?.description,
        buildingUuid: organizationdata?.buildingUuid,
        organizationUuid: e?.organizationUuid,
        mobileUserUuid: tempcardid?.uuid,
        badgeId: typeof e?.badgeId === 'string' ? parseInt(e?.badgeId) : e?.badgeId,
        facilityCode:
          typeof e?.facilityCode === 'string' ? parseInt(e?.facilityCode) : e?.facilityCode,
        format: e?.format,
        doorIds: selectedDoorIds,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        integrationCardDetails: integrationCardDetails
      });
    }
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          setIsCardDefaultValuesLoaded(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-3 gap-3 mr-8'}>
            <Input
              label="First Name"
              name="firstName"
              type="text"
              placeholder="Enter First Name"
              autoFocus
              methods={methods}
            />
            <Input
              label="Last Name"
              name="lastName"
              type="text"
              placeholder="Enter Last Name"
              autoFocus
              methods={methods}
            />
            <div
              className={!!editUUID ? 'flex justify-content-between items-center w-full' : ''}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <div style={{ flexGrow: 1 }}>
                <CustomPhoneInput
                  name="phoneNumber"
                  label="Phone Number"
                  defaultCountry="CA"
                  control={methods.control}
                  errors={methods.formState.errors}
                  placeholder="Enter Phone Number"
                  disabled={!!editUUID}
                />
              </div>
              {!!editUUID && (
                <HelpTooltipIcon
                  message={`To modify phone number, you must delete this occupant and create a new one.`}
                />
              )}
            </div>
          </div>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            <RoleSelectionForm
              Controller={Controller}
              methods={methods}
              editUUID={editUUID}
              accountData={accountData}
              buildingData={buildingData}
              orgData={orgData}
            />
            {emailRequired && (
              <Input
                label="Email"
                name="email"
                type="email"
                placeholder="Enter Email"
                autoFocus
                methods={methods}
                disabled={
                  !!editUUID &&
                  (selectedIntegration === AccessIntegrationName.ICT ||
                    selectedIntegration === AccessIntegrationName.Latch)
                }
              />
            )}
            <CheckboxInput
              label="Is Staff?"
              name="isStaff"
              methods={methods}
              helpTooltipMessage="Check this box if the occupant is a staff member"
            />
          </div>

          {!editUUID && (
            <div className="mt-2 text-sm">
              <label className="text-neutral9 font-semibold">Give Occupant an Access Card?</label>
              <span className="text-neutral9"> (Optional)</span>
              <br />

              <Box
                sx={{
                  marginRight: '32px',
                  marginTop: '16px',
                  marginBottom: '16px',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: '50px',
                  display: 'inline-flex',
                  backgroundColor: colours.selectLightTeal,
                  borderColor: colours.toggleBorderTeal
                }}
              >
                <div
                  style={{
                    cursor:
                      integrationData?.name === AccessIntegrationName.None ? 'not-allowed' : 'auto'
                  }}
                >
                  <StyledToggleButtonGroup
                    value={cardToggleOption}
                    exclusive
                    onChange={handleCardOptionChange}
                    aria-label="access card option"
                    disabled={integrationData?.name === AccessIntegrationName.None}
                  >
                    <ToggleButton
                      value="noCard"
                      aria-label="No access card"
                      sx={{
                        '&.MuiToggleButton-root': {
                          backgroundColor: colours.selectLightTeal,
                          color: 'black',
                          '&:hover': {
                            backgroundColor: colours.selectHoverTeal
                          },
                          paddingTop: '5px',
                          paddingBottom: '5px',
                          textTransform: 'none'
                        },
                        '&.Mui-selected': {
                          backgroundColor: colours.darkTeal2, // Background color for selected button
                          borderRadius: '50px',
                          color: 'white', // Text color for selected button
                          '&:hover': {
                            backgroundColor: colours.darkTeal2
                          }
                        },
                        '&.Mui-disabled': {
                          backgroundColor: colours.selectLightTeal,
                          color: 'gray'
                        }
                      }}
                    >
                      No Access Card
                    </ToggleButton>
                    <ToggleButton
                      value="newCard"
                      aria-label="new card"
                      sx={{
                        '&.MuiToggleButton-root': {
                          backgroundColor: colours.selectLightTeal,
                          color: 'black',
                          '&:hover': {
                            backgroundColor: colours.selectHoverTeal
                          },
                          paddingTop: '5px',
                          paddingBottom: '5px',
                          textTransform: 'none'
                        },
                        '&.Mui-selected': {
                          backgroundColor: colours.seafoam,
                          borderRadius: '50px',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: colours.seafoam
                          }
                        },
                        '&.Mui-disabled': {
                          backgroundColor: colours.selectLightTeal,
                          color: 'gray'
                        }
                      }}
                    >
                      New Card
                    </ToggleButton>
                    {isW26Integration && (
                      <ToggleButton
                        value="existingCard"
                        aria-label="existing card"
                        sx={{
                          '&.MuiToggleButton-root': {
                            backgroundColor: colours.selectLightTeal,
                            color: 'black',
                            '&:hover': {
                              backgroundColor: colours.selectHoverTeal
                            },
                            paddingTop: '5px',
                            paddingBottom: '5px',
                            textTransform: 'none'
                          },
                          '&.Mui-selected': {
                            backgroundColor: colours.seafoam,
                            borderRadius: '50px',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: colours.seafoam
                            }
                          },
                          '&.Mui-disabled': {
                            backgroundColor: colours.selectLightTeal,
                            color: 'gray'
                          }
                        }}
                      >
                        Existing Card
                      </ToggleButton>
                    )}
                  </StyledToggleButtonGroup>
                </div>
              </Box>
            </div>
          )}

          {/* No Card Component */}
          {cardToggleOption === CardToggleOptions.NoCard && !editUUID && (
            <>
              {/* Extra space */}
              <div className="grid grid-cols-1 sm:grid-cols-2" />
            </>
          )}

          {/* New Card Component */}
          {cardToggleOption === CardToggleOptions.NewCard && !editUUID && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8">
              <div className="col-span1 sm:col-span-2 mt-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SelectInput
                    Controller={Controller}
                    control={methods.control}
                    label="Card Format"
                    name="format"
                    defaultValue={getOccupantResult?.format}
                    errors={methods.formState.errors}
                    placeholder="Select a Format"
                    options={filterCardFormats(integrationData?.name)}
                  />
                  <Input
                    methods={methods}
                    label="Description"
                    name="description"
                    type="text"
                    placeholder="Add description..."
                    autoFocus
                  />

                  {/* WaveLynx specific create card fields */}
                  {(selectedCardFormat === CardFormat.W26 ||
                    selectedCardFormat === CardFormat.WAVELYNXWALLET) && (
                    <>
                      <GeneratorInput
                        label="Badge ID"
                        placeholder="12345"
                        validate={{
                          withinRange: (value: number) => {
                            return (
                              (buildingInfo &&
                                !!value &&
                                value <= buildingInfo.maxBadge &&
                                value >= buildingInfo.minBadge) ||
                              `Badge id must be between ${
                                buildingInfo && buildingInfo.minBadge
                              } and ${buildingInfo && buildingInfo.maxBadge}`
                            );
                          }
                        }}
                        width={320}
                        min={buildingInfo?.minBadge || 0}
                        max={buildingInfo?.maxBadge || 0}
                        excluded={cardsData && cardsData?.map((card) => card?.badgeId)}
                        methods={methods}
                        name="badgeId"
                      />
                      <GeneratorInput
                        label="Facility ID"
                        placeholder="12345"
                        validate={{
                          withinRange: (value: number) => {
                            return (
                              (buildingInfo &&
                                !!value &&
                                value <= buildingInfo.maxFac &&
                                value >= buildingInfo.minFac) ||
                              `Badge id must be between ${
                                buildingInfo && buildingInfo.minFac
                              } and ${buildingInfo && buildingInfo.maxFac}`
                            );
                          }
                        }}
                        width={320}
                        min={buildingInfo?.minFac || 0}
                        max={buildingInfo?.maxFac || 0}
                        excluded={[]}
                        methods={methods}
                        name="facilityCode"
                      />
                    </>
                  )}

                  {/* Assa Visionline specific create card fields */}
                  {selectedCardFormat === CardFormat.AVL && (
                    <>
                      <div className="col-span-1 sm:col-span-2 w-auto">
                        <NestedAntdMultiSelect
                          Controller={Controller}
                          control={methods.control}
                          name="doorIds"
                          label="Choose Doors"
                          placeholder="Select Doors"
                          options={Object.values(doorOptions)}
                          errors={methods.formState.errors}
                        />
                      </div>
                      <div className="col-span-1 sm:col-span-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <TimezoneDateTimeRangePicker
                            Controller={Controller}
                            control={methods.control}
                            label="Check-In Date & Time"
                            name="startDate"
                            placeholder="Select Check-In Date & Time"
                            isClearable={false}
                            timezone={buildingTimezone}
                            selectsStart={false}
                            startDate={methods.watch('startDate')}
                            endDate={methods.watch('endDate')}
                            maxDate={moment.tz(new Date(), buildingTimezone).toDate()}
                            errors={methods.formState.errors}
                          />
                          <TimezoneDateTimeRangePicker
                            Controller={Controller}
                            control={methods.control}
                            label="Check-Out Date & Time"
                            name="endDate"
                            placeholder="Select Check-Out Date & Time"
                            isClearable={false}
                            timezone={buildingTimezone}
                            minDate={methods.watch('startDate')}
                            selectsEnd={true}
                            startDate={methods.watch('startDate')}
                            endDate={methods.watch('endDate')}
                            errors={methods.formState.errors}
                          />
                        </div>
                      </div>
                      <SelectInput
                        Controller={Controller}
                        control={methods.control}
                        label="Select Assa Card Type"
                        name="integrationCardDetails.assaVisionline.cardType"
                        errors={methods.formState.errors}
                        placeholder="Select Assa Card Type"
                        options={assaCardTypes}
                      />
                      {isGuestAssaCardType && (
                        <SelectInput
                          Controller={Controller}
                          control={methods.control}
                          label="Select Guest Card Type"
                          name="integrationCardDetails.assaVisionline.guestCardType"
                          errors={methods.formState.errors}
                          placeholder="Select Guest Card Type"
                          options={assaGuestCardTypes}
                          disabled={true}
                        />
                      )}
                    </>
                  )}

                  {/* Latch specific create card fields */}
                  {selectedCardFormat === CardFormat.LATCH && (
                    <>
                      <div className="col-span-1 sm:col-span-2 w-auto">
                        <NestedAntdMultiSelect
                          Controller={Controller}
                          control={methods.control}
                          name="latchDoorIds"
                          label="Choose Doors"
                          placeholder="Select Doors"
                          options={Object.values(latchDoorOptions)}
                          errors={methods.formState.errors}
                        />
                      </div>
                      <div className="col-span-1 sm:col-span-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <TimezoneDateTimeRangePicker
                            Controller={Controller}
                            control={methods.control}
                            label="Check-In Date & Time"
                            name="latchStartDate"
                            placeholder="Select Check-In Date & Time"
                            isClearable={false}
                            timezone={buildingTimezone}
                            selectsStart={false}
                            startDate={methods.watch('latchStartDate')}
                            endDate={methods.watch('latchEndDate')}
                            errors={methods.formState.errors}
                          />
                          <TimezoneDateTimeRangePicker
                            Controller={Controller}
                            control={methods.control}
                            label="Check-Out Date & Time"
                            name="latchEndDate"
                            placeholder="Select Check-Out Date & Time"
                            isClearable={false}
                            timezone={buildingTimezone}
                            minDate={methods.watch('latchStartDate')}
                            selectsEnd={true}
                            startDate={methods.watch('latchStartDate')}
                            endDate={methods.watch('latchEndDate')}
                            errors={methods.formState.errors}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Existing Card Component */}
          {(cardToggleOption === CardToggleOptions.ExistingCard || editUUID) && (
            <>
              <div className="grid grid-cols-1 mr-8">
                <div className="col-span-1 w-auto mt-3">
                  {/* Wavelynx specific choose a card field */}
                  {(isW26Integration && editUUID) || !editUUID ? (
                    isCardDefaultValuesLoaded || !editUUID ? (
                      <MultiSelectInput
                        Controller={Controller}
                        control={methods.control}
                        label="Choose a Card"
                        name="cards"
                        errors={methods.formState.errors}
                        placeholder="Select a Card"
                        options={cardOptions}
                        defaultValue={cardDefaultValues}
                      />
                    ) : (
                      <LoadingSpinner /> // Show loading indicator while default values are loading
                    )
                  ) : null}
                </div>
              </div>
            </>
          )}

          <PrimaryButton
            buttonLabel={entityName}
            iconLeft={<UsersIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
            disabled={disabled}
          />
          {isAVLIntegration && editUUID && (
            <div className="mt-6 border-t border-neutral6">
              <div className="text-sm font-semibold text-neutral5 my-4">LOST PHONE?</div>
              <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
                <SelectInput
                  Controller={Controller}
                  control={methods.control}
                  label="Select Lost Phone"
                  name="lostPhoneId"
                  errors={methods.formState.errors}
                  placeholder="Select Lost Phone"
                  options={phoneOptions}
                />
                <button
                  type="button"
                  className={`primary-btn default !bg-secondary2 mt-[23px] px-6 w-auto gap-2 ${
                    phoneDeactivateDisbaled && 'cursor-not-allowed'
                  }`}
                  onClick={() => {
                    handleDeactivatePhone(methods.getValues()); // Pass form data to `handleDeactivatePhone`
                  }}
                  disabled={phoneDeactivateDisbaled}
                >
                  <PhonelinkEraseIcon style={{ fontSize: 'large' }} /> Deactivate Phone
                </button>
              </div>
            </div>
          )}
          {selectedCardFormat === CardFormat.AVL &&
            cardToggleOption === CardToggleOptions.NewCard && (
              <div className="text-sm text-neutral6 mt-4 p-2 my-8 border border-neutral3 bg-neutral1 rounded w-full md:w-[550px]">
                <p className="mb-2">
                  <span className="font-semibold">Please Note:</span> If you intend to generate a
                  card using the Occupant form for an Occupant of 'AVL' integration type, please
                  note that only 'Joiner' Assa Guest cards can be created through this method. To
                  create an Occupant with an 'Override' Assa Guest card type, please adhere to the
                  following steps:
                </p>
                <ul className="list-disc pl-4 mb-2">
                  <li>First, generate an Occupant without immediately creating a Card.</li>
                  <li>
                    Direct the Occupant to sign in to the B-Line mobile application which will
                    register there device.
                  </li>
                  <li>
                    Then, proceed to create a Assa Guest card of 'Override' type for the respective
                    Occupant by navigating to the Cards page.
                  </li>
                </ul>
                <span className="font-semibold">
                  If a 'Plastic' Card was created first then the Card of 'Joiner' type can be
                  created directly from this form.
                </span>
              </div>
            )}
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
